import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { warehouseAPI } from "../../services/api";
import { Plus, Edit2, Trash2, Save, X, ChevronRight } from "lucide-react";
import { useSubmit } from "../../hooks/useSubmit";
import { useEscClose } from "../../hooks/useEscClose";

// ══════════════════════════════════════════════════════
// کامپوننت‌های خارجی (خارج از SettingsPage)
// ══════════════════════════════════════════════════════

interface StyleProps { dark:boolean; T:any; inp:React.CSSProperties; lbl:React.CSSProperties; }

// ── ردیف دسته‌بندی (بازگشتی) ──
function CatRow({ cat, cats, depth=0, editing, setEditing, onUpdate, onDelete, T, dark, inp, lbl }: any) {
  const children = cats.filter((c:any) => Number(c.parent_id) === Number(cat.id));
  return (
    <>
      <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:10, padding:`10px 16px 10px ${16+depth*24}px`, borderBottom:`1px solid ${T.cardBorder}`, alignItems:"center", background:depth>0?(dark?"rgba(255,255,255,0.01)":"rgba(0,0,0,0.01)"):undefined }}>
        {editing?.id === cat.id ? (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              <input value={editing.name} onChange={e=>setEditing((f:any)=>({...f,name:e.target.value}))} style={inp}/>
              <select value={editing.parent_id||""} onChange={e=>setEditing((f:any)=>({...f,parent_id:e.target.value||null}))} style={{...inp,background:dark?"#1e293b":"#f8faff"}}>
                <option value="">— بدون والد</option>
                {cats.filter((c:any)=>c.id!==cat.id).map((c:any)=><option key={c.id} value={c.id}>{c.parent_id?"↳ ":""}{c.name}</option>)}
              </select>
            </div>
            <div style={{display:"flex",gap:5}}>
              <button onClick={()=>onUpdate(editing)} style={{width:28,height:28,borderRadius:7,border:"none",cursor:"pointer",background:"rgba(16,185,129,0.15)",color:"#10b981",display:"flex",alignItems:"center",justifyContent:"center"}}><Save size={12}/></button>
              <button onClick={()=>setEditing(null)} style={{width:28,height:28,borderRadius:7,border:"none",cursor:"pointer",background:"rgba(100,116,139,0.15)",color:T.subtext,display:"flex",alignItems:"center",justifyContent:"center"}}><X size={12}/></button>
            </div>
          </>
        ) : (
          <>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              {depth>0&&<ChevronRight size={12} color={T.subtext}/>}
              <div style={{width:8,height:8,borderRadius:"50%",background:"#10b981",opacity:depth>0?0.5:1}}/>
              <span style={{fontSize:13,fontWeight:depth===0?700:500,color:T.text}}>{cat.name}</span>
              {cat.parent_name&&<span style={{fontSize:10,color:T.subtext,background:dark?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.05)",padding:"2px 6px",borderRadius:4}}>زیر: {cat.parent_name}</span>}
            </div>
            <div style={{display:"flex",gap:5}}>
              <button onClick={()=>setEditing({...cat})} style={{width:28,height:28,borderRadius:7,border:"none",cursor:"pointer",background:"rgba(59,130,246,0.12)",color:"#3b82f6",display:"flex",alignItems:"center",justifyContent:"center"}}><Edit2 size={12}/></button>
              <button onClick={()=>onDelete(cat.id,cat.name)} style={{width:28,height:28,borderRadius:7,border:"none",cursor:"pointer",background:"rgba(239,68,68,0.12)",color:"#ef4444",display:"flex",alignItems:"center",justifyContent:"center"}}><Trash2 size={12}/></button>
            </div>
          </>
        )}
      </div>
      {children.map((child:any)=>(
        <CatRow key={child.id} cat={child} cats={cats} depth={depth+1} editing={editing} setEditing={setEditing} onUpdate={onUpdate} onDelete={onDelete} T={T} dark={dark} inp={inp} lbl={lbl}/>
      ))}
    </>
  );
}

// ── مدیریت واحدها ──
function UnitManager({ units, onAdd, onUpdate, onDelete, dark, T, inp, lbl }: any) {
  const [form, setForm]       = useState({name:"",symbol:""});
  const [editing, setEditing] = useState<any>(null);
  const { submitting, submit } = useSubmit();
  useEscClose(() => setEditing(null), !!editing);
  const card: React.CSSProperties = { borderRadius:14, padding:20, background:dark?"rgba(255,255,255,0.03)":"#fff", border:`1px solid ${T.cardBorder}`, marginBottom:14 };

  return (
    <div>
      <div style={card}>
        <div style={{fontSize:13,fontWeight:800,color:T.text,marginBottom:14}}>➕ واحد جدید</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:10,alignItems:"end"}}>
          <div><label style={lbl}>نام واحد *</label><input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="مثال: کیلوگرم" style={inp}/></div>
          <div><label style={lbl}>نماد</label><input value={form.symbol} onChange={e=>setForm(f=>({...f,symbol:e.target.value}))} placeholder="مثال: kg" style={{...inp,direction:"ltr" as const}}/></div>
          <button onClick={()=>submit(()=>{ onAdd(form); setForm({name:"",symbol:""}); })} disabled={submitting} style={{display:"flex",alignItems:"center",gap:6,padding:"9px 18px",borderRadius:9,border:"none",background:submitting?"#94a3b8":"linear-gradient(135deg,#10b981,#059669)",color:"white",cursor:submitting?"not-allowed":"pointer",fontSize:13,fontFamily:"inherit",fontWeight:700,whiteSpace:"nowrap"}}><Plus size={14}/>{submitting?"...":"ثبت"}</button>
        </div>
      </div>
      <div style={{...card,padding:0,overflow:"hidden"}}>
        <div style={{padding:"12px 16px",background:dark?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.03)",borderBottom:`1px solid ${T.cardBorder}`,fontSize:11,fontWeight:700,color:T.subtext,display:"grid",gridTemplateColumns:"1fr 1fr auto"}}>
          <span>نام</span><span>نماد</span><span>عملیات</span>
        </div>
        {units.length===0?<div style={{textAlign:"center",padding:40,color:T.subtext}}>هیچ واحدی ثبت نشده</div>:
        units.map((u:any)=>(
          <div key={u.id} style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:10,padding:"11px 16px",borderBottom:`1px solid ${T.cardBorder}`,alignItems:"center"}}>
            {editing?.id===u.id?(
              <>
                <input value={editing.name} onChange={e=>setEditing((f:any)=>({...f,name:e.target.value}))} style={inp}/>
                <input value={editing.symbol||""} onChange={e=>setEditing((f:any)=>({...f,symbol:e.target.value}))} style={{...inp,direction:"ltr" as const}}/>
                <div style={{display:"flex",gap:5}}>
                  <button onClick={()=>submit(()=>{ onUpdate(editing); setEditing(null); })} disabled={submitting} style={{width:28,height:28,borderRadius:7,border:"none",cursor:submitting?"not-allowed":"pointer",background:"rgba(16,185,129,0.15)",color:"#10b981",display:"flex",alignItems:"center",justifyContent:"center"}}><Save size={12}/></button>
                  <button onClick={()=>setEditing(null)} style={{width:28,height:28,borderRadius:7,border:"none",cursor:"pointer",background:"rgba(100,116,139,0.15)",color:T.subtext,display:"flex",alignItems:"center",justifyContent:"center"}}><X size={12}/></button>
                </div>
              </>
            ):(
              <>
                <span style={{fontSize:13,color:T.text,fontWeight:600}}>{u.name}</span>
                <span style={{fontSize:12,color:T.subtext,direction:"ltr" as const}}>{u.symbol||"—"}</span>
                <div style={{display:"flex",gap:5}}>
                  <button onClick={()=>setEditing({...u})} style={{width:28,height:28,borderRadius:7,border:"none",cursor:"pointer",background:"rgba(59,130,246,0.12)",color:"#3b82f6",display:"flex",alignItems:"center",justifyContent:"center"}}><Edit2 size={12}/></button>
                  <button onClick={()=>onDelete(u.id,u.name)} style={{width:28,height:28,borderRadius:7,border:"none",cursor:"pointer",background:"rgba(239,68,68,0.12)",color:"#ef4444",display:"flex",alignItems:"center",justifyContent:"center"}}><Trash2 size={12}/></button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── مدیریت دسته‌بندی ──
function CatManager({ cats, onAdd, onUpdate, onDelete, accentColor, dark, T, inp, lbl }: any) {
  const [form, setForm]       = useState({name:"",parent_id:"",description:""});
  const [editing, setEditing] = useState<any>(null);
  const { submitting, submit } = useSubmit();
  useEscClose(() => setEditing(null), !!editing);
  const card: React.CSSProperties = { borderRadius:14, padding:20, background:dark?"rgba(255,255,255,0.03)":"#fff", border:`1px solid ${T.cardBorder}`, marginBottom:14 };
  const roots = cats.filter((c:any)=>!c.parent_id);

  return (
    <div>
      <div style={card}>
        <div style={{fontSize:13,fontWeight:800,color:T.text,marginBottom:14}}>➕ دسته‌بندی جدید</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:10,alignItems:"end"}}>
          <div><label style={lbl}>نام دسته‌بندی *</label><input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="نام دسته‌بندی..." style={inp}/></div>
          <div>
            <label style={lbl}>زیرمجموعه‌ی</label>
            <select value={form.parent_id} onChange={e=>setForm(f=>({...f,parent_id:e.target.value}))} style={{...inp,background:dark?"#1e293b":"#f8faff"}}>
              <option value="">— دسته اصلی</option>
              {cats.map((c:any)=><option key={c.id} value={c.id}>{c.parent_id?"↳ ":""}{c.name}</option>)}
            </select>
          </div>
          <button onClick={()=>submit(()=>{ onAdd({...form,parent_id:form.parent_id||null}); setForm({name:"",parent_id:"",description:""}); })} disabled={submitting} style={{display:"flex",alignItems:"center",gap:6,padding:"9px 18px",borderRadius:9,border:"none",background:submitting?"#94a3b8":`linear-gradient(135deg,${accentColor},${accentColor}cc)`,color:"white",cursor:submitting?"not-allowed":"pointer",fontSize:13,fontFamily:"inherit",fontWeight:700,whiteSpace:"nowrap"}}><Plus size={14}/>{submitting?"...":"ثبت"}</button>
        </div>
      </div>
      <div style={{...card,padding:0,overflow:"hidden"}}>
        <div style={{padding:"12px 16px",background:dark?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.03)",borderBottom:`1px solid ${T.cardBorder}`,fontSize:11,fontWeight:700,color:T.subtext}}>
          دسته‌بندی‌ها ({cats.length})
        </div>
        {cats.length===0?<div style={{textAlign:"center",padding:40,color:T.subtext}}>هیچ دسته‌بندی ثبت نشده</div>:
          roots.map((cat:any)=>(
            <CatRow key={cat.id} cat={cat} cats={cats} editing={editing} setEditing={setEditing}
              onUpdate={(d:any)=>{ onUpdate(d); setEditing(null); }}
              onDelete={onDelete} T={T} dark={dark} inp={inp} lbl={lbl}
            />
          ))
        }
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// کامپوننت اصلی
// ══════════════════════════════════════════════════════
export default function SettingsPage() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [tab, setTab]         = useState<"units"|"prodCats"|"srvCats">("units");
  const [units, setUnits]     = useState<any[]>([]);
  const [prodCats, setProdCats] = useState<any[]>([]);
  const [srvCats, setSrvCats]   = useState<any[]>([]);
  const [toast, setToast]     = useState<{msg:string;ok:boolean}|null>(null);

  const showToast = (msg:string,ok=true)=>{ setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };

  const loadUnits    = async()=>{ const r=await warehouseAPI.unitsList();       if(r.success) setUnits(r.units); };
  const loadProdCats = async()=>{ const r=await warehouseAPI.productCatsList(); if(r.success) setProdCats(r.categories); };
  const loadSrvCats  = async()=>{ const r=await warehouseAPI.serviceCatsList(); if(r.success) setSrvCats(r.categories); };
  useEffect(()=>{ loadUnits(); loadProdCats(); loadSrvCats(); },[]);

  const inp: React.CSSProperties = { padding:"8px 12px", background:dark?"rgba(255,255,255,0.05)":"#f8faff", border:`1.5px solid ${dark?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)"}`, borderRadius:8, color:T.text, fontSize:13, fontFamily:"'Vazirmatn','Segoe UI',sans-serif", direction:"rtl" as const, outline:"none", boxSizing:"border-box" as const, width:"100%" };
  const lbl: React.CSSProperties = { fontSize:11, fontWeight:700, color:T.subtext, display:"block", marginBottom:4 };

  const subTabs = [
    {id:"units",    label:"📏 واحدها"},
    {id:"prodCats", label:"📦 دسته‌بندی اجناس"},
    {id:"srvCats",  label:"🔧 دسته‌بندی خدمات"},
  ];

  return (
    <div dir="rtl">
      {toast&&<div style={{position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",zIndex:9999,padding:"12px 24px",borderRadius:12,background:dark?"#1e293b":"#fff",border:`2px solid ${toast.ok?"#10b981":"#ef4444"}`,boxShadow:"0 8px 32px rgba(0,0,0,0.25)",color:toast.ok?"#10b981":"#ef4444",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>{toast.msg}</div>}

      <div style={{display:"flex",gap:6,marginBottom:20}}>
        {subTabs.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id as any)} style={{padding:"8px 16px",borderRadius:9,border:`1.5px solid ${tab===t.id?"#10b981":T.cardBorder}`,background:tab===t.id?"rgba(16,185,129,0.1)":"transparent",color:tab===t.id?"#10b981":T.subtext,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>
            {t.label}
          </button>
        ))}
      </div>

      {tab==="units" && (
        <UnitManager units={units} dark={dark} T={T} inp={inp} lbl={lbl}
          onAdd={async(d:any)=>{ if(!d.name.trim()){showToast("نام الزامی است",false);return;} const r=await warehouseAPI.unitAdd(d); if(r.success){showToast("✅ واحد ثبت شد");loadUnits();}else showToast(r.message,false); }}
          onUpdate={async(d:any)=>{ const r=await warehouseAPI.unitUpdate(d); if(r.success){showToast("✅ ویرایش شد");loadUnits();}else showToast(r.message,false); }}
          onDelete={async(id:number,name:string)=>{ if(!window.confirm(`"${name}" حذف شود؟`))return; const r=await warehouseAPI.unitDelete(id); if(r.success){showToast("✅ حذف شد");loadUnits();}else showToast(r.message,false); }}
        />
      )}

      {tab==="prodCats" && (
        <CatManager cats={prodCats} accentColor="#10b981" dark={dark} T={T} inp={inp} lbl={lbl}
          onAdd={async(d:any)=>{ if(!d.name?.trim()){showToast("نام الزامی است",false);return;} const r=await warehouseAPI.productCatAdd(d); if(r.success){showToast("✅ ثبت شد");loadProdCats();}else showToast(r.message,false); }}
          onUpdate={async(d:any)=>{ const r=await warehouseAPI.productCatUpdate(d); if(r.success){showToast("✅ ویرایش شد");loadProdCats();}else showToast(r.message,false); }}
          onDelete={async(id:number,name:string)=>{ if(!window.confirm(`"${name}" حذف شود؟`))return; const r=await warehouseAPI.productCatDelete(id); if(r.success){showToast("✅ حذف شد");loadProdCats();}else showToast(r.message,false); }}
        />
      )}

      {tab==="srvCats" && (
        <CatManager cats={srvCats} accentColor="#3b82f6" dark={dark} T={T} inp={inp} lbl={lbl}
          onAdd={async(d:any)=>{ if(!d.name?.trim()){showToast("نام الزامی است",false);return;} const r=await warehouseAPI.serviceCatAdd(d); if(r.success){showToast("✅ ثبت شد");loadSrvCats();}else showToast(r.message,false); }}
          onUpdate={async(d:any)=>{ const r=await warehouseAPI.serviceCatUpdate(d); if(r.success){showToast("✅ ویرایش شد");loadSrvCats();}else showToast(r.message,false); }}
          onDelete={async(id:number,name:string)=>{ if(!window.confirm(`"${name}" حذف شود؟`))return; const r=await warehouseAPI.serviceCatDelete(id); if(r.success){showToast("✅ حذف شد");loadSrvCats();}else showToast(r.message,false); }}
        />
      )}
    </div>
  );
}